import React, { useState, useEffect, useMemo } from "react";
import {
  Mail,
  CheckCircle,
  Building,
  Users,
  Phone,
  UserCog,
  Globe,
} from "lucide-react";
import toast from "react-hot-toast";
import { fetchEmailTypes, updateEmailTypes } from "../../api/emailtype";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

// Icon shown next to each category section.
const CATEGORY_ICONS = {
  Property: Building,
  Leads: Users,
  Communication: Phone,
  Staff: UserCog,
  "Buyer Portal": Globe,
};

/**
 * Settings -> Email tab.
 *
 * Lists every automated email the platform sends, grouped by area, with a
 * short description of each email's purpose and an on/off switch. Turning a
 * switch off stops that email type from being sent anywhere on the platform.
 */
const EmailSettings = () => {
  const [emailTypes, setEmailTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await fetchEmailTypes();
        if (!cancelled) setEmailTypes(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) toast.error("Failed to load email settings");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleEmailType = (key) => {
    setEmailTypes((prev) =>
      prev.map((t) => (t.key === key ? { ...t, enabled: !t.enabled } : t))
    );
  };

  // Minimum-score threshold for the few email types that gate on one. Blank
  // clears it (send on every match); the server clamps to the valid range.
  const setThreshold = (key, value) => {
    const next =
      value === "" ? null : Math.max(1, Math.min(10, parseInt(value, 10) || 1));
    setEmailTypes((prev) =>
      prev.map((t) => (t.key === key ? { ...t, threshold: next } : t))
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates = emailTypes.map((t) => ({
        key: t.key,
        enabled: t.enabled,
        // Only send a threshold for types that expose one (keeps other rows lean).
        ...(t.threshold_label ? { threshold: t.threshold ?? null } : {}),
      }));
      const result = await updateEmailTypes(updates);
      if (result?.email_types) {
        setEmailTypes(result.email_types);
      }
      toast.success("Email settings updated successfully!");
    } catch (error) {
      toast.error("Failed to update email settings");
    } finally {
      setIsSaving(false);
    }
  };

  // Group email types by category, preserving backend order.
  const groupedCategories = useMemo(() => {
    const groups = [];
    const byName = {};
    emailTypes.forEach((type) => {
      if (!byName[type.category]) {
        byName[type.category] = { name: type.category, items: [] };
        groups.push(byName[type.category]);
      }
      byName[type.category].items.push(type);
    });
    return groups;
  }, [emailTypes]);

  const enabledCount = emailTypes.filter((t) => t.enabled).length;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#55C5D0]"></div>
        <p className="text-gray-600">Loading email settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Intro */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#55C5D0]" />
            <CardTitle className="text-lg">Email Notifications</CardTitle>
          </div>
          <CardDescription>
            These are the automated emails the platform sends to admins,
            agents and buyers. Turn any email type off to stop it being sent.
            Account emails like sign-in verification and password resets are
            always sent and are not listed here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{enabledCount}</span>{" "}
            of{" "}
            <span className="font-semibold text-gray-900">
              {emailTypes.length}
            </span>{" "}
            email types are currently enabled.
          </p>
        </CardContent>
      </Card>

      {/* One card per category */}
      {groupedCategories.map((group) => {
        const Icon = CATEGORY_ICONS[group.name] || Mail;
        return (
          <Card key={group.name}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-[#55C5D0]" />
                <CardTitle>{group.name}</CardTitle>
              </div>
              <CardDescription>
                {group.items.length} email type
                {group.items.length === 1 ? "" : "s"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {group.items.map((type) => (
                <div
                  key={type.key}
                  className="flex items-start justify-between gap-4 border rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {type.name}
                      </span>
                      <Badge variant="outline">{type.recipient}</Badge>
                      {!type.enabled && (
                        <Badge variant="secondary">Off</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {type.description}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Switch
                      checked={type.enabled}
                      onCheckedChange={() => toggleEmailType(type.key)}
                      aria-label={`Toggle ${type.name}`}
                    />
                    {type.threshold_label && (
                      <div className="flex items-center justify-end gap-2">
                        <label className="text-xs text-gray-500 text-right max-w-[190px] leading-tight">
                          {type.threshold_label}
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={type.threshold ?? ""}
                          onChange={(e) => setThreshold(type.key, e.target.value)}
                          disabled={!type.enabled}
                          placeholder="—"
                          className="w-16 border border-gray-300 rounded-md px-2 py-1 text-sm text-right disabled:opacity-50"
                          aria-label={`Minimum score for ${type.name}`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}

      {emailTypes.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-gray-500">
            No email types found.
          </CardContent>
        </Card>
      )}

      {/* Save */}
      <div className="flex justify-end pt-2">
        <Button
          size="lg"
          className="px-8 bg-[#55C5D0] hover:bg-[#3bb3be] text-white"
          onClick={handleSave}
          disabled={isSaving || emailTypes.length === 0}
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EmailSettings;
